'use client';

import type { RowData } from '@tanstack/react-table';
import type { Table } from '@/lib/table-features';
import { useTranslations } from 'next-intl';
import { FileText } from 'lucide-react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { DataTableToolbar } from '@/components/ui/table/data-table-toolbar';
import type { DynamicForm } from '@/features/dynamic-forms';

interface SearchFiltersBarProps<TData extends RowData> {
  /** Virtual table whose filterable columns drive the structured filters. */
  table: Table<TData>;
  /** Active registration forms shown in the picker. */
  allForms: DynamicForm[];
  /** Current `dynamic_form_id` URL value ('', 'all', or a numeric string). */
  selectedFormId: string;
  /** Called when the user picks a form ('all' or a form-id string). */
  onFormChange: (value: string) => void;
}

/**
 * Table-toolbar-style filter bar for the smart-search page. Combines a
 * dynamic-form picker (mirrors the registration listing's selector) with the
 * column-driven structured filters rendered by {@link DataTableToolbar}. The
 * view options button is suppressed — this table never displays rows, it only
 * hosts filter state.
 */
export function SearchFiltersBar<TData extends RowData>({
  table,
  allForms,
  selectedFormId,
  onFormChange
}: SearchFiltersBarProps<TData>) {
  const t = useTranslations('search');

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex items-center gap-2'>
        <FileText className='text-muted-foreground size-4' />
        <Select value={selectedFormId || 'all'} onValueChange={onFormChange}>
          <SelectTrigger className='h-8 w-auto min-w-[200px] text-xs'>
            <SelectValue placeholder={t('filters.form')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>{t('filters.allForms')}</SelectItem>
            {allForms.map((form) => (
              <SelectItem key={form.id} value={String(form.id)}>
                {form.name}
                {form.is_default && (
                  <span className='text-muted-foreground ms-1'>
                    ({t('filters.defaultForm')})
                  </span>
                )}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <DataTableToolbar table={table} showViewOptions={false} />
    </div>
  );
}
